import { css, cx } from "@emotion/css";
import { Link, useLocation } from "react-router-dom";
import LogoMark from "../../ui/logo-mark/logo-mark";
import { useAuth } from "../../../features/auth/hooks/use-auth";

const wrapper = css({
  position: 'sticky',
  top: 0,
  zIndex: 50,
  background: 'var(--primary-color)',
  width: '100%',
});

const inner = css({
  maxWidth: '1440px',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 var(--spacing-lg)',
  height: '5.5rem',
});

const brand = css({
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  padding: '0.25rem 0',
  ':focus-visible': {
    outline: '2px solid var(--pc-500)',
    outlineOffset: '2px',
    borderRadius: 4,
  },
});

const navList = css({
  listStyle: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 'clamp(0rem, 5vw, 5rem)',
  margin: 0,
  padding: 0,
});

const linkBase = css({
  position: 'relative',
  fontSize: 'var(--fs-400)',
  fontWeight: 500,
  textDecoration: 'none',
  color: 'var(--secondary-color)',
  padding: '0.25rem 0',
  transition: 'color .2s ease',
  ':hover': {
    color: 'var(--pc-400)',
  },
  ':focus-visible': {
    outline: '2px solid var(--pc-500)',
    outlineOffset: '2px',
    borderRadius: 2,
  },
});

const activeLink = css({
  color: 'var(--secondary-color)',
  fontWeight: 600,
});

const authButton = css({
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5ch',
  padding: '0.55rem 1.1rem',
  borderRadius: 999,
  background: 'var(--pc-600)',
  color: 'var(--primary-color)',
  border: '1px solid var(--pc-600)',
  cursor: 'pointer',
  lineHeight: 1.1,
  transition: 'background .2s ease, box-shadow .2s ease, transform .15s ease',
  textDecoration: 'none',
  boxShadow: 'var(--shadow-200)',
  ':hover': {
    background: 'var(--pc-500)',
  },
  ':active': {
    transform: 'translateY(1px)',
  },
  ':focus-visible': {
    outline: '2px solid var(--pc-400)',
    outlineOffset: '2px',
  },
});

interface NavItem {
  label: string;
  to: string;
  match?: string | RegExp;
}

const navItems: NavItem[] = [
  { label: 'Pagina Inicial', to: '/', match: /^\/$/ },
  { label: 'Eventos', to: '/#eventos', match: /^\/#eventos/ },
  { label: 'Ajuda', to: '/contato', match: /^\/contato/ },
];

interface NavbarDesktopProps extends React.HTMLAttributes<HTMLElement> {
  className?: string;
  ref?: React.Ref<HTMLElement>;
}

export function NavbarDesktop({ className, ref, ...props }: NavbarDesktopProps) {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();

  const isActive = (item: NavItem) =>
    item.match instanceof RegExp
      ? item.match.test(pathname)
      : pathname === item.match;

  return (
    <header ref={ref} className={cx(wrapper, className)} {...props}>
      <div className={inner}>
        <Link to="/" className={brand} aria-label="Página inicial">
          <LogoMark />
        </Link>

        <nav aria-label="Principal">
          <ul className={navList}>
            {navItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={isActive(item) ? `${linkBase} ${activeLink}` : linkBase}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li>
              {isAuthenticated ? (
                <Link to="/perfil" className={authButton}>
                  Minha conta
                </Link>
              ) : (
                <Link to="/entrar" className={authButton}>
                  Entrar <span aria-hidden="true">|</span> Registrar
                </Link>
              )}
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
}

export default NavbarDesktop;
